import { safeSessionStorage } from '@toss/storage';

import type { PaymentFormData } from '../../Checkout/schema';
import type { PaymentEntry } from '../constants/paymentIntent';

type PaymentDraft = Omit<PaymentFormData, 'termsAgreed'>;

/**
 * 저장된 초안에는 **소유자를 함께 새긴다.** sessionStorage 는 탭 단위라 로그인 경계를 넘어
 * 살아남는데, 계정 전환(`useOnSuccessLogin` 의 `window.location.replace` 풀로드)은 로그아웃조차
 * 거치지 않는다. 소유자 없이 두면 B 계정 주문이 A 계정의 이메일·소속·수량으로 만들어진다.
 */
interface StoredDraft {
  ownerId: string;
  values: Partial<PaymentDraft>;
}

/** `v1` 은 소유자가 없던 형태다. 버전을 올려 남은 엔트리를 자연히 무시한다. */
const storageKey = (key: string) => `quiz-payment-draft:v2:${key}`;

export const getPaymentDraftKey = (entry: PaymentEntry) =>
  entry.kind === 'purchase'
    ? `purchase:${entry.plan}:${entry.startDate ?? ''}`
    : `${entry.kind}:${
        entry.target.kind === 'specific'
          ? entry.target.quizPlanHashId
          : 'active'
      }`;

/**
 * @param ownerId 현재 로그인 사용자(`user.quizUserId`). 없으면(미인증·스테일 캐시 부팅) 읽지
 * 않는다 — 초안을 잃는 쪽이 다른 계정으로 새는 쪽보다 낫다.
 */
export const getPaymentDraft = (
  key: string,
  ownerId: string | undefined,
): Partial<PaymentDraft> | undefined => {
  if (!ownerId) return undefined;

  const raw = safeSessionStorage.get(storageKey(key));
  if (!raw) return undefined;

  let stored: unknown;
  try {
    stored = JSON.parse(raw);
  } catch {
    return undefined;
  }

  const draft = stored as StoredDraft | null;
  if (!draft || typeof draft !== 'object' || !draft.values) return undefined;

  if (draft.ownerId !== ownerId) {
    // 다른 계정을 거친 값은 남겨 둘 이유가 없다. 원래 주인이 돌아와도 되살리지 않는다.
    safeSessionStorage.remove(storageKey(key));
    return undefined;
  }

  return draft.values;
};

export const setPaymentDraft = (
  key: string,
  ownerId: string | undefined,
  values: Partial<PaymentFormData>,
) => {
  if (!ownerId) return;

  const { termsAgreed: _termsAgreed, ...draft } = values;
  const stored: StoredDraft = { ownerId, values: draft };
  safeSessionStorage.set(storageKey(key), JSON.stringify(stored));
};

export const clearPaymentDraft = (key: string) =>
  safeSessionStorage.remove(storageKey(key));
